import { useEffect, useState } from 'react';
import { Page } from '../components/Page.js';

type Entry = { seq: number; op: string; ts: string; entity_type: string | null; entity_id: string | null; summary: string };

const fmt = (t: string) => t.replace('T', ' ').replace(/\.\d+Z$/, ' UTC');

// Which ledger ops count as "police interactions".
const KIND: Record<string, { label: string; badge: string }> = {
  notify: { label: 'Notification', badge: 'ok' },
  reconcile: { label: 'Reconciliation', badge: 'warn' },
  import: { label: 'Police list', badge: 'mute' },
  disclose: { label: 'Disclosure', badge: 'warn' },
};

function relevant(e: Entry): boolean {
  if (e.op === 'notify') return true;
  if (e.op === 'reconcile') return true;
  if (e.op === 'import' && e.entity_type === 'police_snapshot') return true;
  if (e.op === 'disclose' && (e.entity_type === 'grant' || e.entity_type === 'device' || e.entity_type === 'bank_account' || e.entity_type === 'bank_card')) return true;
  return false;
}

export function PoliceLog() {
  const [entries, setEntries] = useState<Entry[]>([]);

  useEffect(() => {
    fetch('/api/ledger?limit=300').then((r) => r.json()).then((d) => setEntries((d.entries ?? []).filter(relevant))).catch(() => {});
  }, []);

  return (
    <Page eyebrow="Compliance" title="Police log"
      actions={<span className="pill pill-ok"><span className="dot dot-ok" />every entry is anchored</span>}>
      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          A single chronological record of every interaction touching the police: notifications you made,
          police-list snapshots you imported, reconciliation decisions, and any disclosure to an inspector.
          Each line is a tamper-evident, anchored ledger entry — this is the spine of your audit trail.
        </p>
      </section>

      <div className="card">
        <div className="panel-head"><h3>Interactions</h3><span className="hint">{entries.length} · newest first</span></div>
        <div className="ledger">
          {entries.length === 0 && <div style={{ padding: 16, color: 'var(--ink3)', fontSize: 13 }}>Nothing recorded yet. Notifications, reconciliations and disclosures will appear here.</div>}
          {entries.map((e) => {
            const k = KIND[e.op] ?? { label: e.op, badge: 'mute' };
            return (
              <div className="ledger-row" key={e.seq}>
                <span className={`badge badge-${k.badge}`}>{k.label}</span>
                <div className="ledger-main">
                  <div className="ledger-text">{e.summary || e.entity_type || e.op}</div>
                  <div className="ledger-time">{fmt(e.ts)} · ledger seq {e.seq}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="panel-foot mono">filtered view of the append-only ledger · notify · reconcile · police-snapshot · disclose</div>
      </div>
    </Page>
  );
}
